import React from "react";
import { BannerCaseStudy } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import Link from "next/link";
import { renderRichText } from "@/lib/richText";

interface BlogCompCaseStudyProps {
  blok?: BannerCaseStudy & SbBlokData;
}

const BannerCaseStudyComponent: React.FC<BlogCompCaseStudyProps> = ({ blok }) => {
  if (!blok) return null;

  const { banner_type, title, description, cta_button, cta_button_link, image, topic, blog_banner_text } = blok;

  const renderImage = (width = 219, height = 305, className = "js-lazy-load") => {
    if (!image?.filename) {
      return width === 64 ? null : (
        <div style={{ width: `${width}px`, height: `${height}px` }} className="placeholder-image" />
      );
    }

    return (
      <Image
        src={image.filename}
        alt={image.alt || title || ""}
        title={image.title || title || ""}
        width={width}
        height={height}
        className={className}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        loading="lazy"
        quality={75}
      />
    );
  };

  const renderCTAButton = (additionalClasses = "") => {
    if (!cta_button || !cta_button_link?.url) return null;

    const buttonClass = `btn btn-lg ${banner_type === "red_bg" ? "btn-white" : "btn-white"} ${additionalClasses}`.trim();

    return (
      <Link
        href={cta_button_link.url}
        className={buttonClass}
        target={cta_button_link.target}
      >
        {cta_button}
      </Link>
    );
  };

  const renderBannerContent = (centered = false) => (
    <div className={`banner-content${centered ? " text-center" : ""}`}>
      {title && <h2 className="text-white">{title}</h2>}
      {description && <p className="lead text-white mb-3">{description}</p>}
      {renderCTAButton("js-cta-btn")}
    </div>
  );

  switch (banner_type) {
    case "cyber_essentials":
      return (
        <div {...storyblokEditable(blok)} className="banner-blue-chequered py-4 mb-4">
          <div className="container">
            <div className="banner-content">
              {renderImage(219, 305, "bg-white p-4 rounded-4")}
              {renderBannerContent()}
            </div>
          </div>
        </div>
      );

    case "banner_white":
      return (
        <div {...storyblokEditable(blok)} className="banner-blue-chequered py-4 mb-4">
          <div className="container">
            <div className="banner-content">
              {renderImage(219, 305, "js-lazy-load bg-white p-4 rounded-4")}
              {title && <h2 className="text-white">{title}</h2>}
              {description && <p className="lead text-white mb-3">{description}</p>}
              {renderCTAButton("js-cta-btn")}
            </div>
          </div>
        </div>
      );

    case "banner_white_right":
      return (
        <div {...storyblokEditable(blok)} className="py-4 mb-4 banner_white_right">
          <div className="container">
            <div className="banner-content">
              {title && <h2 className="text-white">{title}</h2>}
              {description && <p className="lead text-white mb-3">{description}</p>}
              {renderImage(480, 185, "js-lazy-load bg-white p-2 rounded-4")}
              {renderCTAButton("js-cta-btn")}
            </div>
          </div>
        </div>
      );

    case "center_align":
      return (
        <div {...storyblokEditable(blok)} className="brand-section mb-4">
          <div className="container row mx-auto align-items-center py-4 py-md-5">
            <div className="col-24 lead text-center">
            {title && <h2 className="text-white">{title}</h2>}
            {description && <p className="lead text-white mb-3">{description}</p>}
            {blog_banner_text && (
              <div
                className="lead"
                dangerouslySetInnerHTML={{ __html: renderRichText(blog_banner_text) }}
              />
            )}
              {renderCTAButton("js-cta-btn")}
            </div>
          </div>
        </div>
      );

    case "red_bg":
      return (
        <div {...storyblokEditable(blok)} className="bg-primary text-center py-5 mb-4">
          <div className="container">
            {renderCTAButton("btn-lg js-cta-btn")}
          </div>
        </div>
      );

    case "box_cta":
      return (
        <div {...storyblokEditable(blok)} className="container py-4 py-md-5 mb-4">
          <div className="numbers-bg-section brand-section component-box-cta rounded-4 p-4 p-lg-5">
            <div className="row align-items-center">
              <div className="col-24 col-lg-10">
                {topic && <span className="pretitle mb-2">{topic}</span>}
                {title && <h2 className="text-white mb-4">{title}</h2>}
                {renderImage(64, 64, "img-fluid component-box-cta-image mb-3 mb-lg-0")}
              </div>
              <div className="col-24 col-lg-10 offset-lg-4">
                {description && (
                  <p className="component-box-cta-content text-white mb-3">{description}</p>
                )}
                {cta_button && cta_button_link?.url && (
                  <p className="mt-4 mb-0">
                    {renderCTAButton()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      );

    case "case_study_straight":
      return (
        <div {...storyblokEditable(blok)} className="numbers-bg-section py-4 mb-4">
          <div className="container">
            <div className="two-thirds-centered d-flex">
              {image?.filename && (
                <Image
                  src={image.filename}
                  alt={image.alt || title || ""}
                  width={160}
                  height={160}
                  className="me-3"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              )}
              <div className="text-center-mobile">
                {title && <h2 className="text-white">{title}</h2>}
                {description && <p className="lead text-white mb-3">{description}</p>}
                {cta_button && cta_button_link?.url && (
                  <Link
                    href={cta_button_link.url || "#" }
                    className="btn btn-primary"
                    target={cta_button_link.target}
                  >
                    {cta_button}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      );

    case "download_now":
      return (
        <div {...storyblokEditable(blok)} className="container py-4 py-md-5 mb-4">
          <div className="numbers-bg-section brand-section component-box-cta-image-left rounded-4">
            <div className="row align-items-stretch justify-content-end g-0">
              <div
                className="col-24 col-lg-9 px-4 py-2 px-xl-5 text-center d-flex align-items-center justify-content-center img-blur component-box-cta-image-left-img">
                {image?.filename && (
                  <>
                    <Image
                      src={image.filename}
                      alt={image.alt || title || ""}
                      width={465}
                      height={658}
                      className="scroll-trigger fade-in duration-1s img-blur-bg on"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                      quality={75}
                    />
                    <Image
                      src={image.filename}
                      alt={image.alt || title || ""}
                      width={465}
                      height={658}
                      className="scroll-trigger slide-up delay-1s duration-1s img-blur-fg m-4 on"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                      quality={75}
                    />
                  </>
                )}
              </div>
              <div className="col-24 col-lg-14 offset-lg-1 p-4 p-xl-5">
                <h2 className="text-white mb-4 exclude-toc">
                  {topic && <span className="pretitle mb-2">{topic}</span>}
                  {title}
                </h2>
                {description && (
                  <p className="component-box-cta-image-left-content text-white mb-3">
                    {description}
                  </p>
                )}
                {cta_button && cta_button_link?.url && (
                  <p className="mt-4 mb-0">
                    {renderCTAButton()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      );

    case "case_study_red":
      return (
        <div {...storyblokEditable(blok)} className="bg-primary text-white pt-4 banner-industry-reports-download mb-4">
          <div className="container">
            <div className="row align-items-end">
              <div className="col-24 col-md-10 col-lg-8 text-center banner-industry-reports-download-img px-2">
                {image?.filename && (
                  <Image
                    src={image.filename}
                    alt={image.alt || title || ""}
                    width={465}
                    height={658}
                    className="img-fluid"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="lazy"
                    quality={75}
                  />
                )}
              </div>
              <div
                className="col-24 col-md-14 col-lg-14 pb-4 pt-4 text-center text-md-start banner-industry-reports-download-content">
                {title && (
                  <h2
                    className="text-white"
                    id={title.toLowerCase().replace(/\s+/g, "-")}
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="lead text-white mb-4">{description}</p>
                )}
                {cta_button && cta_button_link?.url && (
                  <Link
                    href={cta_button_link.url}
                    className="btn btn-lg btn-secondary"
                    target={cta_button_link.target}
                  >
                    {cta_button}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      );


    case "gdpr":
      return (
        <div {...storyblokEditable(blok)} className="numbers-bg-section py-4 mb-4">
          <div className="container">
            <div className="two-thirds-centered d-flex">
              {image?.filename && (
                <Image
                  src={image.filename}
                  alt={image.alt || "Clipboard with checklist icon"}
                  width={120}
                  height={124}
                  className="me-3"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              )}
              <div className="text-center-mobile">
                {title && <h3 className="white-text">{title}</h3>}
                {description && (
                  <p className="brand-light-text">{description}</p>
                )}
                {blog_banner_text && (
                  <div
                    className="brand-light-text"
                    dangerouslySetInnerHTML={{ __html: renderRichText(blog_banner_text) }}
                  />
                )}
                {cta_button && cta_button_link?.url && (
                  <Link
                    href={cta_button_link.url}
                    className="btn btn-lg btn-secondary"
                    target={cta_button_link.target}
                  >
                    {cta_button}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      );

    default:
      return (
        <div {...storyblokEditable(blok)} className="banner-case-study py-4 py-md-5">
          <div className="container">
            {renderImage()}
            {renderBannerContent()}
          </div>
        </div>
      );
  }
};

export default BannerCaseStudyComponent;
